import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { Inbox, CheckCircle2, XCircle, Sparkles, Send, ArrowRight, Filter, CheckCheck } from "lucide-react";

const NAV = [
  {to:"/funder/terminal",label:"Deal flow"},
  {to:"/funder/inbox",label:"Inbox"},
  {to:"/funder/saved",label:"Saved"},
  {to:"/funder/mandate",label:"Mandate"},
  {to:"/funder/analytics",label:"Analytics"},
  {to:"/funder/pipeline",label:"Pipeline"},
  {to:"/funder/billing",label:"Billing"},
];

const TYPE_META = {
  interest_approved: { label: "Interest approved", color: "green", Icon: CheckCircle2, cta: "Open data room" },
  interest_declined: { label: "Interest declined", color: "neutral", Icon: XCircle, cta: "Back to deal flow" },
  outreach_received: { label: "Founder is interested", color: "[#0A8B85]", Icon: Send, cta: "Review business" },
  new_match: { label: "New match", color: "amber", Icon: Sparkles, cta: "View business" },
};

const COLOR_CLASSES = {
  green: "bg-green-50 text-green-700",
  amber: "bg-amber-50 text-amber-700",
  "[#0A8B85]": "bg-[#0A8B85]/10 text-[#0A8B85]",
};
const colorClass = (key) => COLOR_CLASSES[key] || "bg-neutral-100 text-neutral-600";

function timeAgo(iso) {
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  } catch { return ""; }
}

export default function FunderInbox() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get("/funder/inbox");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.post(`/funder/alerts/${id}/read`);
    setData(d => ({ ...d,
      items: d.items.map(i => i.alert_id === id ? { ...i, read: true } : i),
      unread: Math.max(0, d.unread - 1),
    }));
  };
  const markAll = async () => {
    setBusy(true);
    try {
      await Promise.all((data?.items || []).filter(i => !i.read).map(i => api.post(`/funder/alerts/${i.alert_id}/read`)));
      await load();
    } finally { setBusy(false); }
  };

  const items = (data?.items || []).filter(i => filter === "all" || i.type === filter);

  return (
    <AppShell nav={NAV}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-[#0A8B85]"/>
            <h1 className="text-3xl font-bold text-neutral-900">Funder Inbox</h1>
          </div>
          <p className="mt-1 text-sm text-neutral-600">
            Everything that happens to the businesses on your radar — approvals, outreach from founders, and new matches.
          </p>
        </div>
        {data?.unread > 0 && (
          <button onClick={markAll} disabled={busy} data-testid="inbox-readall"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
            <CheckCheck className="h-4 w-4"/> Mark all read ({data.unread})
          </button>
        )}
      </div>

      {/* Filter pills */}
      <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="h-4 w-4 shrink-0 text-neutral-500"/>
        <FilterChip current={filter} value="all" onClick={setFilter} count={data?.total} label="All"/>
        {Object.entries(data?.by_type || {}).map(([t, n]) => (
          <FilterChip key={t} current={filter} value={t} onClick={setFilter} count={n} label={TYPE_META[t]?.label || t.replaceAll("_"," ")}/>
        ))}
      </div>

      {/* Feed */}
      <div className="mt-6 rounded-2xl border border-neutral-200 bg-white" data-testid="inbox-feed">
        {data === null && <div className="p-10 text-center text-sm text-neutral-500">Loading…</div>}
        {data !== null && items.length === 0 && (
          <div className="p-12 text-center">
            <Inbox className="mx-auto h-8 w-8 text-neutral-300"/>
            <p className="mt-3 text-sm text-neutral-500">
              Nothing here yet. As founders approve your interest, accept outreach, or new businesses match your mandate, you'll see them here.
            </p>
          </div>
        )}
        <ul className="divide-y divide-neutral-100">
          {items.map(i => {
            const meta = TYPE_META[i.type] || { label: i.type, color: "neutral", Icon: Inbox, cta: null };
            const Icon = meta.Icon;
            const dest = i.company_id ? `/funder/sme/${i.company_id}` : "/funder/terminal";
            return (
              <li key={i.alert_id} data-testid={`inbox-item-${i.alert_id}`}
                  className={"flex items-start gap-3 p-4 " + (i.read ? "opacity-70" : "")}>
                <div className={"mt-0.5 rounded-lg p-2 " + colorClass(meta.color)}>
                  <Icon className="h-4 w-4"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-neutral-500">{meta.label}</span>
                    {!i.read && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-red-600">New</span>}
                    <span className="ml-auto text-[11px] text-neutral-500">{timeAgo(i.created_at)}</span>
                  </div>
                  <p className="mt-1 text-sm text-neutral-900">{i.message}</p>
                  {i.company && (
                    <p className="mt-1 text-xs text-neutral-500">
                      {i.company.company_name} · {i.company.sector} · {i.company.country} · {i.company.stage}
                      {typeof i.company.readiness_score === "number" && (
                        <span className="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-bold text-neutral-700">
                          IRS {i.company.readiness_score}
                        </span>
                      )}
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-3">
                    {meta.cta && (
                      <button onClick={() => { nav(dest); markRead(i.alert_id); }}
                        data-testid={`inbox-cta-${i.alert_id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#0A8B85] hover:underline">
                        {meta.cta} <ArrowRight className="h-3 w-3"/>
                      </button>
                    )}
                    {!i.read && (
                      <button onClick={() => markRead(i.alert_id)} data-testid={`inbox-read-${i.alert_id}`}
                        className="text-xs text-neutral-500 hover:text-neutral-800">Mark read</button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </AppShell>
  );
}

function FilterChip({ current, value, onClick, count, label }) {
  const active = current === value;
  return (
    <button onClick={() => onClick(value)} data-testid={`inbox-filter-${value}`}
      className={"inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors " +
        (active ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50")}>
      {label}
      {typeof count === "number" && <span className={"rounded-full px-1.5 text-[10px] " + (active ? "bg-white/20" : "bg-neutral-100 text-neutral-600")}>{count}</span>}
    </button>
  );
}
